/**
 * Voice Dictation Component
 *
 * Provides voice-to-text input for radiology reports
 * Uses Web Speech API with Spanish (Colombia) support
 *
 * Features:
 * - Microphone toggle button
 * - Visual feedback during listening
 * - Voice command support
 * - Fallback for unsupported browsers
 *
 * @module presentation/components
 */

import { Mic, MicOff, AlertCircle } from 'lucide-react'
import { Button } from './ui/Button'
import { useDictation, useDictationAvailable } from '@/application/dictation'

/**
 * Voice dictation props
 */
export interface VoiceDictationProps {
  /** Callback when text is transcribed */
  onTranscript: (text: string) => void
  /** Whether dictation is disabled */
  disabled?: boolean
  /** Language for recognition */
  lang?: string
  /** Size variant */
  size?: 'sm' | 'default' | 'lg'
  /** Additional class names */
  className?: string
}

/**
 * Voice dictation button with real-time transcription
 */
export function VoiceDictation({
  onTranscript,
  disabled = false,
  lang = 'es-CO',
  size = 'default',
  className = '',
}: VoiceDictationProps) {
  const isSupported = useDictationAvailable()

  const { isListening, error, toggleListening, transcript } = useDictation({
    lang,
    processCommands: true,
    onResult: (text) => {
      onTranscript(text)
    },
  })

  // Browser doesn't support speech recognition
  if (!isSupported) {
    return (
      <Button
        type="button"
        variant="outline"
        size={size === 'sm' ? 'sm' : 'default'}
        disabled
        title="Dictado por voz no soportado en este navegador"
        className={className}
      >
        <MicOff className="h-4 w-4 text-[var(--foreground-muted)]" />
      </Button>
    )
  }

  // Error state
  if (error) {
    return (
      <Button
        type="button"
        variant="outline"
        size={size === 'sm' ? 'sm' : 'default'}
        onClick={toggleListening}
        disabled={disabled}
        title={error}
        className={`${className} border-red-200 dark:border-red-800`}
      >
        <AlertCircle className="h-4 w-4 text-red-500" />
      </Button>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant={isListening ? 'default' : 'outline'}
        size={size === 'sm' ? 'sm' : 'default'}
        onClick={toggleListening}
        disabled={disabled}
        title={isListening ? 'Detener dictado' : 'Iniciar dictado por voz'}
        className={`${className} ${isListening ? 'animate-pulse bg-red-500 hover:bg-red-600' : ''}`}
      >
        {isListening ? (
          <Mic className="h-4 w-4" />
        ) : (
          <Mic className="h-4 w-4" />
        )}
      </Button>

      {/* Interim transcript indicator */}
      {isListening && transcript && (
        <span className="text-xs text-[var(--foreground-muted)] italic max-w-[200px] truncate">
          {transcript}...
        </span>
      )}

      {/* Listening indicator */}
      {isListening && !transcript && (
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-xs text-[var(--foreground-muted)]">Escuchando...</span>
        </span>
      )}
    </div>
  )
}

/**
 * Compact voice dictation button for inline use
 */
export function VoiceDictationButton({
  onTranscript,
  disabled = false,
  className = '',
}: Omit<VoiceDictationProps, 'size'>) {
  const isSupported = useDictationAvailable()

  const { isListening, toggleListening } = useDictation({
    lang: 'es-CO',
    processCommands: true,
    onResult: onTranscript,
  })

  if (!isSupported) {
    return null
  }

  return (
    <button
      type="button"
      onClick={toggleListening}
      disabled={disabled}
      title={isListening ? 'Detener dictado' : 'Iniciar dictado por voz'}
      className={`p-1 rounded hover:bg-[var(--background-subtle)] transition-colors ${
        isListening ? 'text-red-500' : 'text-[var(--foreground-muted)]'
      } ${className}`}
    >
      <Mic className={`h-4 w-4 ${isListening ? 'animate-pulse' : ''}`} />
    </button>
  )
}
